import { galerieALaction } from '../../auth/sujet.js';
import { IcalPanel } from './IcalPanel.js';
import { RendezVousDialog } from './RendezVousDialog.js';
import { Badge, Button, Checkbox } from '@artosera/ui';
import { useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { EvenementAgenda } from '../../api/agenda.js';
import { estApiError } from '../../api/errors.js';
import { BarreCalendrier } from '../../composants/calendrier/BarreCalendrier.js';
import { Calendrier } from '../../composants/calendrier/Calendrier.js';
import {
  aujourdhuiParis,
  bornesDepuisInstants,
  bornesDepuisJours,
  deplacerAncre,
  formaterJour,
  plageDeVue,
  VUES_CALENDRIER,
  type EvenementCalendrier,
  type TeinteCalendrier,
} from '../../composants/calendrier/periodes.js';
import { useVueCalendrier } from '../../composants/calendrier/useVueCalendrier.js';
import { useShellContext } from '../shell/context.js';
import { useAgendaPlage } from './useAgendaPlage.js';
import styles from './AgendaPage.module.css';
import { Verrouillable } from '../module/LectureSeule.js';

const TEINTE_PAR_SORTE: Record<EvenementAgenda['sorte'], TeinteCalendrier> = {
  vernissage: 'accent',
  exposition: 'accent',
  foire: 'accent',
  echeance: 'reserved',
  'rendez-vous': 'neutral',
  evenement: 'neutral',
};

function lienDe(e: EvenementAgenda): string {
  if (e.lien.type === 'evenement') return `evenements/${e.lien.id}`;
  return e.lien.type === 'contrat'
    ? 'artistes/contrats'
    : `${e.lien.type === 'document' ? 'coffre' : e.lien.type === 'exposition' ? 'expositions' : e.lien.type === 'offre' ? 'offres' : e.lien.type === 'mouvement' ? 'mouvements' : 'contrats'}/${e.lien.id}`;
}

/** Un événement de l'API en événement de calendrier (journées entières ou à l'heure). */
function versCalendrier(
  e: EvenementAgenda,
  galerieSlug: string,
  ouvrirRendezVous: (id: string) => void,
): EvenementCalendrier {
  const bornes = e.journeeEntiere
    ? bornesDepuisJours(e.debut.slice(0, 10), e.fin?.slice(0, 10) ?? null)
    : bornesDepuisInstants(new Date(e.debut), e.fin ? new Date(e.fin) : null);
  return {
    id: e.id,
    titre: e.titre,
    ...bornes,
    teinte: TEINTE_PAR_SORTE[e.sorte],
    ...(e.sorte === 'vernissage' ? { evidence: true } : {}),
    detail: e.lieu,
    ...(e.lien.type === 'rendez-vous'
      ? { onSelect: () => ouvrirRendezVous(e.lien.id) }
      : { href: `/${galerieSlug}/${lienDe(e)}` }),
  };
}

export function AgendaPage(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const { galerieSlug, galerie } = useShellContext();
  const { vue, ancre, aller } = useVueCalendrier('agenda');
  const [dialogue, setDialogue] = useState<string | null | undefined>();
  const [creneau, setCreneau] = useState<{ jour: string; heure?: string } | undefined>();
  const [sources, setSources] = useState<string[] | undefined>();
  const peutEcrire = galerieALaction(galerie, 'agenda:write');
  const aujourdhui = aujourdhuiParis();
  const agenda = useAgendaPlage(galerieSlug, plageDeVue(vue, ancre), sources);
  const format = (jour: string, options: Intl.DateTimeFormatOptions) =>
    formaterJour(jour, langue, options);
  const parJour = new Map<string, EvenementAgenda[]>();
  for (const e of agenda.data?.evenements ?? []) {
    const jour = e.journeeEntiere
      ? e.debut
      : new Intl.DateTimeFormat('en-CA', {
          timeZone: 'Europe/Paris',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(new Date(e.debut));
    const liste = parJour.get(jour) ?? [];
    liste.push(e);
    parJour.set(jour, liste);
  }
  const evenementsCalendrier = useMemo(
    () =>
      (agenda.data?.evenements ?? []).map((e) =>
        versCalendrier(e, galerieSlug, (id) => {
          setCreneau(undefined);
          setDialogue(id);
        }),
      ),
    [agenda.data, galerieSlug],
  );
  const heureAffichee = (instant: string) =>
    new Intl.DateTimeFormat(i18n.language, {
      timeZone: 'Europe/Paris',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(instant));
  return (
    <div className={styles.page} data-ui="agenda-page">
      <header className={styles.entete} data-ui="page-header">
        <h1 className={styles.titre}>{t('regie:agenda.titre')}</h1>
        <Verrouillable action="agenda:write" ouvert={peutEcrire}>
          <Button
            onClick={() => {
              setCreneau(undefined);
              setDialogue(null);
            }}
          >
            {t('regie:agenda.rendezVous.nouveau')}
          </Button>
        </Verrouillable>
      </header>
      <BarreCalendrier
        vue={vue}
        vues={VUES_CALENDRIER}
        ancre={ancre}
        langue={langue}
        onVue={(suivante) => aller(suivante, ancre)}
        onDeplacer={(sens) => aller(vue, sens === 0 ? undefined : deplacerAncre(vue, ancre, sens))}
      />
      <div className={styles.filtres} data-ui="filters">
        {agenda.data?.sources.map((id) => (
          <Checkbox
            key={id}
            label={t(`regie:agenda.sources.${id}`)}
            checked={sources === undefined || sources.includes(id)}
            onCheckedChange={(actif) =>
              setSources(
                actif
                  ? [...(sources ?? agenda.data!.sources), id]
                  : (sources ?? agenda.data!.sources).filter((s) => s !== id),
              )
            }
          />
        ))}
      </div>
      {agenda.isError ? (
        <p role="alert">
          {estApiError(agenda.error) ? agenda.error.message : t('commun:erreurReseau')}
        </p>
      ) : null}
      {vue === 'liste' ? (
        <div className={styles.corps}>
          <div data-ui="agenda-days">
            {[...parJour].map(([jour, evenements]) => (
              <section className={styles.jour} key={jour}>
                <h3>{format(jour, { weekday: 'long', day: 'numeric', month: 'long' })}</h3>
                <ul
                  className={styles.liste}
                  aria-label={format(jour, { day: 'numeric', month: 'long' })}
                >
                  {evenements.map((e) => (
                    <li
                      className={styles.evenement}
                      key={e.id}
                      data-ui="agenda-event"
                      data-sorte={e.sorte}
                    >
                      <span className={styles.heure}>
                        {e.journeeEntiere
                          ? t('regie:agenda.touteLaJournee')
                          : heureAffichee(e.debut)}
                      </span>
                      <Badge>{t(`regie:agenda.sortes.${e.sorte}`)}</Badge>
                      {e.lien.type === 'rendez-vous' ? (
                        <button
                          className={styles.lien}
                          onClick={() => {
                            setCreneau(undefined);
                            setDialogue(e.lien.id);
                          }}
                        >
                          {e.titre}
                        </button>
                      ) : (
                        <Link className={styles.lien} to={`/${galerieSlug}/${lienDe(e)}`}>
                          {e.titre}
                        </Link>
                      )}
                      {e.lieu ? <span>{e.lieu}</span> : null}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
            {!agenda.isLoading && !parJour.size ? <p>{t('regie:agenda.vide')}</p> : null}
          </div>
          <IcalPanel galerieSlug={galerieSlug} />
        </div>
      ) : (
        <div className={styles.corpsCalendrier}>
          <Calendrier
            vue={vue}
            ancre={ancre}
            evenements={evenementsCalendrier}
            aujourdhui={aujourdhui}
            langue={langue}
            onAller={aller}
            {...(peutEcrire
              ? {
                  onCreer: (jour: string, heure?: string) => {
                    setCreneau({ jour, ...(heure ? { heure } : {}) });
                    setDialogue(null);
                  },
                }
              : {})}
          />
          <IcalPanel galerieSlug={galerieSlug} />
        </div>
      )}
      <RendezVousDialog
        open={dialogue !== undefined}
        onOpenChange={(o) => {
          if (!o) {
            setDialogue(undefined);
            setCreneau(undefined);
          }
        }}
        {...(dialogue ? { rendezVousId: dialogue } : {})}
        {...(creneau ? { jourInitial: creneau.jour } : {})}
        {...(creneau?.heure ? { heureInitiale: creneau.heure } : {})}
      />
    </div>
  );
}
